import { useFormik } from "formik";
import {FC, ReactNode, ChangeEvent, useState, useRef} from "react";
import {wrapClick, classNames, withRoles} from "utils";
import {IMeterDeliveryRequestFormSchema} from "./schema";
import {DocumentDuplicateIcon} from "@heroicons/react/24/outline";
import {FilePreview} from "../../cards";
import axios from "axios";
import toast, {LoaderIcon} from "react-hot-toast";
import {useMutation, useReactiveVar} from "@apollo/client";
import {UPDATE_DOCUMENTATION} from "../../../pages/settings/documentation";
import {currentUserVar} from "../../../apollo/cache/auth";

interface DocumentationInfoFormProps {
  handleNext: (values: any) => void;
  handlePrevious: () => void;
  initialValues: any
  values: IMeterDeliveryRequestFormSchema;
  handleCancel: () => void;
  meterContractor: any;
  refetchMeterContractor: () => void;
}

const fileNameMap = {
  approvedTechnicalDocument: "Approved Technical Document",
  calibrationCertificate: "Calibration Certificate",
  typeTestReport: "Type Test Report",
  ISO14001: "ISO 14001: Environmental Management System Standard",
  ISO45001: "ISO 45001: Occupational Health and Safety Management System",
  ISO9001: "ISO 9001",
  schematicWiringDrawing: "Schematic Wiring Drawing",
  installationOperationalAndMaintenanceManuals: "Installation, Operation and Maintenance Manuals",
  detailedPartListAndMaterialsOfConstruction: "Detailed Parts List and Materials of Construction",
}

const DocumentationInfoForm: FC<DocumentationInfoFormProps> = ({
  initialValues,
  handleNext,
  handleCancel,
  meterContractor,
  refetchMeterContractor,
}) => {

  const form = useFormik({
    initialValues,
    onSubmit: (values) => {
      handleNext(values);
    },
    onReset: () => {
      handleCancel?.();
    },
  });

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="space-y-6 divide-y divide-gray-200 p-6 flex-1 overflow-y-auto">
        <div>
          <span className="text-xs font-light">Documentation Information</span>
          <div className="grid grid-cols-3 gap-6 mt-2">
            {Object.keys(fileNameMap).map(
              (key) =>
                <DocumentCard
                  refetchMeterContractor={refetchMeterContractor}
                  document={meterContractor?.documentation?.[key]}
                  href={key}
                  key={key}
                />
            )}
          </div>
        </div>
      </div>
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
        <button
          type="button"
          data-invalid={!form.isValid}
          onClick={wrapClick(form.handleSubmit)}
          className="w-full inline-flex justify-center data-invalid:bg-gray-200 data-invalid:border-gray-400 data-invalid:cursor-not-allowed data-invalid:text-gray-600 rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Next
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={wrapClick(form.resetForm)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

type DocumentCardProps = {
  document?: {
    url: string,
    uploadedAt: Date,
  },
  href: string,
  refetchMeterContractor: () => void;
}

export const DocumentCard = ({ document, href, refetchMeterContractor }: DocumentCardProps) => {

  const [uploadingFile, setUploadingFile] = useState(false)
  const isUploaded = !!document?.url;
  const inputRef = useRef<HTMLInputElement>(null)
  const currentUser = useReactiveVar(currentUserVar);

  const [updateDocumentation, { loading: loadingDocumentation }] = useMutation(UPDATE_DOCUMENTATION);
  const mutateDocumentation = async (url: string) => {
    await updateDocumentation({
      variables: {
        id: currentUser?.contractor?._id,
        documentation: {
          [href]: url
        }
      }
    }).then(({ data }) => {
      if (data.updateMeterContractorDocumentation._id) {
        refetchMeterContractor();
        toast(JSON.stringify({ type: "success", title: "Documentation Updated Successfully" }));
      }
      else {
        toast(JSON.stringify({ type: "error", title: "Could not update Documentation" }));
      }
    });

  }

  const Container = isUploaded
    ? ({ children }: { children: ReactNode}) =>
      <FilePreview
        url={document?.url}
        trigger={
          ({ setOpen }) => (
            <div
              className={"flex flex-col gap-3 cursor-pointer"}
              onClick={() => setOpen(true)}
            >
              {children}
            </div>
          )
        }
      />
    : ({ children }: { children: ReactNode}) =>
      <div
        className={"flex flex-col gap-3"}
      >{children}</div>

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if(!file) return;

    const data = new FormData();
    data.append("file", file);

    setUploadingFile(true)
    axios.post("/upload", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      }
    })
    .then(async ({ data: url }) => {
      await mutateDocumentation(url)
    })
    .catch((err) => {
      toast(JSON.stringify({ type: "error", title: "An error occurred while uploading the file" }))
    })
    .finally(() => setUploadingFile(false));
  };

  return (
    <div className={"flex-1 min-h-full"}>
      <div className={`h-full flex-1 flex flex-col justify-between gap-3 pt-8 pb-4 px-4 ${isUploaded ? "border-green-400" : "border-red-400" } border rounded-md shadow-md`}>
        <Container>
          <div className={"w-fit flex-shrink-0 aspect-square mx-auto"}>
            <DocumentDuplicateIcon
              strokeWidth={0.6}
              className={classNames(
                "w-20",
                isUploaded ? "text-green-600" : "text-red-500 "
              )}
            />
          </div>
          <div className={"flex flex-col gap-2 items-center"}>
            <div className={"text-md font-medium text-center text-gray-700 "}>{fileNameMap[href as keyof typeof fileNameMap] || "N/A"}</div>
            <span
              className={classNames(
                "font-medium text-sm",
                isUploaded ? "text-green-600" : "text-red-500"
              )}
            >
              {isUploaded ? "Complete" : "Incomplete"}
            </span>
          </div>
        </Container>
        <div>
          {
            withRoles(["Super"])(
              <>
                <button
                  className={"flex gap-4 justify-center w-full"}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    inputRef?.current?.click()
                  }}
                >
                  <label htmlFor={`file-upload-${href}`} className="cursor-pointer border-gray-300 border-2 hover:border-gray-400 duration-150 bg-gray-50 hover:bg-gray-100  w-full flex justify-center p-2 rounded">
                    {
                      (uploadingFile || loadingDocumentation)
                          ? (
                            <span className={"h-[20px] w-[30px] grid place-items-center aspect-square rounded-full  duration-150"}>
                              <LoaderIcon className={'white animate-spin w-4'} />
                            </span>
                        ) : (
                          <div className={"text-sm text-gray-700 font-medium"}>
                            Upload Document
                          </div>
                        )
                      }
                  </label>
                </button>
                <input
                  type="file"
                  ref={inputRef}
                  disabled={uploadingFile || loadingDocumentation}
                  id={`file-upload-${href}`}
                  onChange={(e) => handleFileChange(e)}
                  className="hidden"
                />
              </>
            )
          }
        </div>
      </div>
    </div>
  )

}

export default DocumentationInfoForm;
